import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import api from '../services/apiService';
import { useAppKitAccount } from '@reown/appkit/react';

// Query Keys - Centralized query key management
export const queryKeys = {
  wallet: {
    all: ['wallet'],
    connection: () => [...queryKeys.wallet.all, 'connection'],
    rewards: (address) => [...queryKeys.wallet.all, 'rewards', address],
  },
  game: {
    all: ['game'],
    rooms: () => [...queryKeys.game.all, 'rooms'],
    room: (roomId) => [...queryKeys.game.all, 'room', roomId],
    players: (roomId) => [...queryKeys.game.all, 'players', roomId],
    leaderboard: () => [...queryKeys.game.all, 'leaderboard'],
  },
  user: {
    all: ['user'],
    profile: (address) => [...queryKeys.user.all, 'profile', address],
    stats: (address) => [...queryKeys.user.all, 'stats', address],
  },
  stats: {
    all: ['stats'],
    general: () => [...queryKeys.stats.all, 'general'],
  },
};

// Wallet Queries
export const useWalletConnection = () => {
  const { address, isConnected } = useAccount();
  
  return useQuery({
    queryKey: queryKeys.wallet.connection(),
    queryFn: async () => {
      return {
        isConnected,
        address: isConnected ? address : null,
      };
    },
    enabled: true,
    staleTime: 1000 * 30, // 30 seconds
    retry: 1,
  });
};

export const useUserRewards = () => {
  const { address } = useAppKitAccount();
  const queryFn =  async () => {
      // console.log({address});
    try {
      // Convert address to lowercase for database consistency
      const response = await api.get(`/api/rewards/${address}`);
      const data = await response.data;
      // console.log({data});

      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to fetch rewards');
      }
    } catch (error) {
      console.error('❌ Error fetching rewards:', error);
      throw error;
    }
  }


  return useQuery({
    queryKey: queryKeys.wallet.rewards(address),
    queryFn:queryFn,
    enabled: !!address,
    staleTime: 1000 * 30, // 30 seconds
  });
};

// Game Queries
export const useGameRooms = () => {
  return useQuery({
    queryKey: queryKeys.game.rooms(),
    queryFn: async () => {
      // This would typically fetch from your backend API
      // For now, returning mock data
      return {
        publicRooms: [],
        privateRooms: [],
      };
    },
    staleTime: 1000 * 10, // 10 seconds
  });
};

export const useGameRoom = (roomId) => {
  return useQuery({
    queryKey: queryKeys.game.room(roomId),
    queryFn: async () => {
      if (!roomId) throw new Error('Room ID is required');
      // Fetch specific room data from backend
      return {
        id: roomId,
        players: [],
        status: 'waiting',
        settings: {},
      };
    },
    enabled: !!roomId,
    staleTime: 1000 * 5, // 5 seconds
  });
};

export const useGamePlayers = (roomId) => {
  return useQuery({
    queryKey: queryKeys.game.players(roomId),
    queryFn: async () => {
      if (!roomId) throw new Error('Room ID is required');
      // Fetch players in room from backend
      return [];
    },
    enabled: !!roomId,
    staleTime: 1000 * 2, // 2 seconds
    refetchInterval: 2000, // Refetch every 2 seconds while component is mounted
  });
};

export const useLeaderboard = () => {
  return useQuery({
    queryKey: queryKeys.game.leaderboard(),
    queryFn: async () => {
      // Fetch leaderboard data from backend
      return [];
    },
    staleTime: 1000 * 60, // 1 minute
  });
};

// User Queries
export const useUserProfile = (address) => {
  return useQuery({
    queryKey: queryKeys.user.profile(address),
    queryFn: async () => {
      if (!address) throw new Error('Address is required');
      // Fetch user profile from backend
      return {
        address,
        username: '',
        gamesPlayed: 0,
        gamesWon: 0,
        totalRewards: 0,
      };
    },
    enabled: true, // Always enabled, returns mock data when no address
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useUserStats = (address) => {
  return useQuery({
    queryKey: queryKeys.user.stats(address),
    queryFn: async () => {
      // If no address or address is truncated, return mock data
      if (!address || address.length < 40) {
        // console.log('Using mock data for stats - address not available or truncated');
        return {
          success: true,
          stats: {
            totalGames: { value: 12, change: "+8%", icon: "puzzle-piece", color: "blue" },
            winRate: { value: "75%", change: "+5.2%", icon: "trophy", color: "green" },
            totalEarnings: { value: "$240", change: "+12%", icon: "dollar-sign", color: "yellow" },
            averageScore: { value: 85, change: "+8%", icon: "bar-chart", color: "purple" }
          },
          detailedStats: {
            totalGames: 12,
            wonGames: 9,
            lostGames: 3,
            winRate: 75,
            totalEarnings: 240,
            averageScore: 85,
            pendingClaims: 2,
            completedClaims: 7,
            totalScore: 1020
          },
          totalGames: 12,
          wins: 9,
          losses: 3,
          winRate: 75,
          averageScore: 85,
          totalEarnings: 240,
          pendingClaims: 2,
          completedClaims: 7,
          totalScore: 1020,
          rank: 'Pro',
          currentStreak: 3,
          bestStreak: 5
        };
      }
      
      try {
        // Convert address to lowercase for database consistency
        const normalizedAddress = address?.toLowerCase();
        const response = await api.get(`/api/stats/${normalizedAddress}`);
        const data = await response.data;

        if (data.success) {
          return {
            // Dashboard format for UI components
            stats: data.stats,
            // Detailed stats for calculations
            detailedStats: data.detailedStats,
            // Raw data for compatibility
            totalGames: data.detailedStats.totalGames,
            wins: data.detailedStats.wonGames,
            losses: data.detailedStats.lostGames,
            winRate: data.detailedStats.winRate,
            averageScore: data.detailedStats.averageScore,
            totalEarnings: data.detailedStats.totalEarnings,
            // Additional data
            pendingClaims: data.detailedStats.pendingClaims,
            completedClaims: data.detailedStats.completedClaims,
            totalScore: data.detailedStats.totalScore,
            // Metadata
            walletAddress: data.walletAddress,
            timestamp: data.timestamp
          };
        } else {
          throw new Error(data.error || 'Failed to fetch user statistics');
        }
      } catch (error) {
        console.error('❌ Error fetching user statistics:', error);
        throw error;
      }
    },
    enabled: true, // Always enabled, returns mock data when no address
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 2,
  });
};

// General Statistics Query
export const useGeneralStats = () => {
  return useQuery({
    queryKey: queryKeys.stats.general(),
    queryFn: async () => {
      try {
        const response = await api.get('/api/stats');
        const data = await response.data;

        return {
          totalGames: data.totalGames,
          activeGames: data.activeGames,
          totalPlayers: data.totalPlayers,
          timestamp: data.timestamp
        };
      } catch (error) {
        console.error('❌ Error fetching general statistics:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });
};